<template>
  <div class="cgu-page">
    <header class="top-banner">
      <div class="banner-content">
        <img :src="`${$logoDir}logo-auth.png`" alt="HAXO" class="logo" />
      </div>
    </header>

    <main class="container">
      <article class="cgu-card">

        <template v-if="locale === 'fr'">
          <section class="intro-section">
            <h1>Conditions générales d'utilisation de l'application mobile HAXO OPEN</h1>
            <p class="subtitle">Utilisateurs finaux</p>
            <p class="version">Version du 13 avril 2026</p>

            <p>
              L'application HAXO OPEN (« l'Application ») vous est mise à disposition dans le cadre de la relation contractuelle conclue entre d'une part, l'installateur des Produits HAXO, et d'autre part, votre employeur ou toute autre entité pour le compte ou au bénéfice de laquelle vous utilisez l'application HAXO OPEN (« le Gestionnaire »).
            </p>
            <p>
              Vous accédez ainsi à l'Application en votre qualité d'utilisateur autorisé par le Gestionnaire, pour ses besoins et sous sa responsabilité, dans les limites des droits, habilitations et paramétrages qui vous ont été attribués.
            </p>
            <p>
              Les présentes conditions générales d'utilisation (« CGU ») ont pour objet de définir les modalités et conditions dans lesquelles vous êtes autorisé à accéder à l'application HAXO OPEN et à l'utiliser. Il est toutefois rappelé que la relation contractuelle est conclue entre l'installateur des Produits HAXO et le Gestionnaire exclusivement.
            </p>
          </section>

          <section>
            <h2>Définitions</h2>
            <p>À chaque fois qu'ils seront utilisés dans ces CGU, au singulier ou au pluriel, les termes ci-dessous auront la définition suivante :</p>

            <dl class="definitions-list">
              <div>
                <dt>Application</dt>
                <dd>application mobile éditée par HAXO, dénommée HAXO OPEN, accessible sur smartphone compatible, permettant à l'Utilisateur autorisé d'utiliser certaines fonctionnalités de contrôle d'accès rattachées aux équipements du Gestionnaire.</dd>
              </div>
              <div>
                <dt>Compte</dt>
                <dd>l'espace personnel de l'Utilisateur, créé par le Gestionnaire, permettant son accès à l'Application avec des Identifiants.</dd>
              </div>
              <div>
                <dt>Conditions Générales ou CGU</dt>
                <dd>les présentes conditions générales applicables à l'Utilisateur, déterminant les conditions d'utilisation de l'Application par l'Utilisateur.</dd>
              </div>
              <div>
                <dt>Gestionnaire</dt>
                <dd>personne morale ou personne physique qui utilise les solutions HAXO pour ses besoins professionnels propres et qui autorise l'Utilisateur à accéder à l'Application, via la création et la gestion des habilitations et le paramétrage des droits d'accès.</dd>
              </div>
              <div>
                <dt>HAXO</dt>
                <dd>société par actions simplifiée, dont le siège social est situé 368 Chemin du Chevalier, Quartier Bonneval, 83470 Saint Maximin la Sainte Baume, immatriculée au Registre du Commerce et des Sociétés de Draguignan sous le numéro 934 957 820, représentée par son Président agissant et ayant les pouvoirs nécessaires en cette qualité.</dd>
              </div>
              <div>
                <dt>Identifiants</dt>
                <dd>adresse e-mail de l'Utilisateur et le mot de passe qu'il choisit ou utilise pour accéder à l'Application, ainsi que, le cas échéant, tout autre moyen d'authentification mis en place.</dd>
              </div>
              <div>
                <dt>Lecteur</dt>
                <dd>tout équipement matériel compatible permettant le contrôle d'accès et, le cas échéant, la lecture d'un QR code fixe ou tournant avec l'Application.</dd>
              </div>
              <div>
                <dt>Produits HAXO</dt>
                <dd>les matériels, logiciels, applications, interfaces et services conçus, édités, commercialisés ou opérés par HAXO, directement ou indirectement.</dd>
              </div>
              <div>
                <dt>QR code tournant</dt>
                <dd>code visuel à durée de validité limitée, renouvelé périodiquement, permettant l'authentification de l'Utilisateur sur un lecteur compatible.</dd>
              </div>
              <div>
                <dt>Utilisateur</dt>
                <dd>toute personne physique à laquelle le Gestionnaire a accordé un droit d'accès à l'Application pour ses besoins autorisés.</dd>
              </div>
              <div>
                <dt>Terminal</dt>
                <dd>appareil mobile de l'Utilisateur (dont il est le propriétaire unique ou a le contrôle), équipé d'un système d'exploitation compatible Android ou iOS, sur lequel l'Application est installée et utilisée.</dd>
              </div>
            </dl>
          </section>

          <section>
            <h2>Objet</h2>
            <p>Les présentes CGU ont pour objet de définir les conditions d'installation, d'accès et d'utilisation de l'Application par tout Utilisateur autorisé par le Gestionnaire, client d'un installateur de Produits HAXO.</p>
            <p>L'Utilisateur utilise l'Application au nom, pour le compte ou au bénéfice du Gestionnaire, et sous la responsabilité de ce dernier.</p>
            <p>HAXO se réserve le droit de faire évoluer l'Application et de modifier les présentes CGU à tout moment. En cas de modification des CGU ou de l'Application, l'Utilisateur en sera informé par tout moyen utile au sein de l'Application ou par tout autre moyen approprié.</p>
          </section>

          <section>
            <h2>Description de l'Application et accès</h2>
            <h3>Description de l'Application</h3>
            <p>L'Application permet à des personnes physiques autorisées par le Gestionnaire, au moyen d'un compte personnel, d'accéder à certaines fonctionnalités de contrôle d'accès associées aux équipements déployés par le Gestionnaire, notamment :</p>
            <ul>
              <li>La consultation des portes auxquelles l'Utilisateur est autorisé à accéder ;</li>
              <li>L'ouverture à distance de certaines portes, lorsque cette fonctionnalité est activée ;</li>
              <li>L'affichage du résultat d'une demande d'ouverture ;</li>
              <li>L'utilisation d'un QR code fixe ou tournant à des fins d'authentification sur un Lecteur ;</li>
              <li>La gestion de préférences d'affichage, notamment les portes mises en favoris.</li>
            </ul>
            <p>Lorsqu'elle est activée par le Gestionnaire, la fonctionnalité d'ouverture à distance permet à l'Utilisateur de transmettre une demande d'ouverture pour certaines portes auxquelles il est habilité. Une demande d'ouverture ne vaut pas garantie d'ouverture effective. Son traitement dépend notamment des autorisations affectées à l'Utilisateur, de l'état de fonctionnement des équipements concernés, de la disponibilité des réseaux et infrastructures techniques, ou des règles de sécurité ou de contrôle configurées par le Gestionnaire.</p>
            <p>Certaines demandes d'ouverture à distance peuvent être soumises à une vérification de la position géographique de l'Utilisateur, si cette fonctionnalité a été activée par l' Installateur pour le Lecteur concerné et si l'Utilisateur a autorisé l'accès à la localisation de son Terminal.</p>
            <p>Dans ce cas, l'Application peut vérifier si le Terminal utilisé se trouve dans un périmètre défini autour du Lecteur concerné, afin de permettre ou de refuser la demande d'ouverture.</p>
            <p>L'Utilisateur est informé que :</p>
            <ul>
              <li>La vérification de position peut être obligatoire pour certains accès lorsque l' Installateur l'a activée ;</li>
              <li>L'absence d'autorisation de géolocalisation, la désactivation des services de localisation, une précision GPS insuffisante, une indisponibilité réseau ou toute incohérence de position peuvent empêcher l'ouverture à distance ;</li>
              <li>La géolocalisation n'emporte pas suivi continu de l'Utilisateur : elle peut être limitée à la vérification opérée au moment d'une demande d'ouverture, selon la configuration technique retenue sur son Terminal.</li>
            </ul>
            <p>Selon le paramétrage retenu par le Gestionnaire, l'Application peut permettre à l'Utilisateur d'afficher un QR code tournant utilisé à des fins d'authentification auprès d'un Lecteur compatible.</p>
            <p>Lorsque le QR code est tournant, sa durée de validité est limitée.</p>

            <h3>Accès à l'Application</h3>
            <p>L'Application est réservée aux Utilisateurs préalablement enregistrés dans le système et autorisés par le Gestionnaire.</p>
            <p>Depuis l'application, l' Utilisateur crée son Compte en renseignant son adresse e-mail, son mot de passe et sa méthode d'authentification. L'Utilisateur reçoit alors un e-mail contenant un lien lui permettant d'activer son compte. Le lien fourni étant valide pendant 24 heures.</p>
            <p>Le Gestionnaire invite alors l' Utilisateur en saisissant son adresse e-mail depuis son interface de gestion HAXO KEY.</p>
            <p>L'Utilisateur doit disposer d'un Terminal compatible et des versions logicielles, systèmes et paramétrages nécessaires au bon fonctionnement de l'Application ainsi que d'un accès au réseau Internet.</p>
            <p>L'Utilisateur accède à l'Application après l'avoir téléchargée sur son Terminal depuis la plateforme de téléchargement correspondant au système d'exploitation de son Terminal, et se connecte à son Compte avec ses Identifiants.</p>
            <p>L'Utilisateur reconnaît que les Identifiants sont strictement personnels et confidentiels. Il s'interdit, en conséquence, de les communiquer ou de les partager avec des tiers. Pour éviter une utilisation frauduleuse des Identifiants, l'Utilisateur devra choisir un mot de passe permettant un niveau de sécurité élevé selon les standards et recommandations en vigueur. En cas de perte, de vol, de compromission ou d'utilisation non autorisée du Terminal ou des Identifiants, l'Utilisateur doit informer sans délai le Gestionnaire.</p>
            <p>Les coûts d'équipement, d'abonnement télécom, d'accès au réseau et plus généralement tous les moyens techniques nécessaires à l'utilisation de l'Application restent à la charge exclusive de l'Utilisateur ou du Gestionnaire, selon leur organisation interne.</p>
            <p>Le Terminal doit être équipé d'une version du système d'exploitation compatible, telle qu'indiquée sur la page de téléchargement de l'Application. L'Application peut être mise à jour. Le cas échéant, les prérequis techniques nécessaires à l'installation ou à l'utilisation d'une version mise à jour de l'Application seront précisés sur la page correspondante.</p>

            <h3>Règles de bon usage de l'Application</h3>
            <p>L'Utilisateur s'engage à utiliser l'Application conformément à sa destination, aux présentes CGU et aux instructions communiquées ainsi qu'à respecter les politiques internes, règles de sécurité et consignes du Gestionnaire.</p>
            <p>Il est interdit de chercher à contourner les mécanismes d'authentification, d'autorisation ou de sécurité, d'utiliser l'Application pour détourner les habilitations et règles d'accès définies par le Gestionnaire, notamment en permettant à des tiers d'entrer par les portes déverrouillées par l'Utilisateur, de reproduire, transmettre, capter ou partager un QR code avec un tiers, ou encore de perturber le fonctionnement de l'Application, des réseaux, des serveurs ou des équipements associés.</p>
          </section>

          <section>
            <h2>Droits de propriété intellectuelle</h2>
            <p>HAXO est titulaire de tous droits, titres ou intérêts ou bénéficie des autorisations utiles concernant les Produits HAXO, l'Application et plus généralement les signes distinctifs d'HAXO.</p>
            <p>L'Utilisateur reconnait que les droits de propriété intellectuelle et industrielle, droits d'auteur, brevets, marques, secrets commerciaux, savoir-faire, idées, concepts et inventions, couverts ou non par un titre de propriété intellectuelle, concernant les Produits HAXO, les signes distinctifs et l'Application, y compris mais sans limitation, toutes modifications, traductions, adaptations, améliorations, corrections, mises à jour ou nouvelles versions, œuvres dérivées, compilations, savoir-faire technique, sont et demeurent réservés à tout moment à HAXO.</p>
            <p>Sous réserve de l'existence et du maintien en vigueur d'un contrat conclu entre le Gestionnaire et le l'installateur des Produits HAXO, l'Utilisateur bénéficie au titre des présentes du droit personnel, non-exclusif, non-transférable, pour la durée du contrat conclu par le Gestionnaire, d'installer et d'utiliser l'Application sous sa forme exécutable sur son Terminal, dans la stricte limite des actes nécessaires à l'utilisation de l'Application exclusivement pour les besoins internes du Gestionnaire pour l'utilisation des Produits HAXO à l'exclusion de toute autre destination.</p>
            <p>Toute utilisation de l'Application non-autorisée dans ces CGU est interdite. En particulier, mais sans limitation, l'Utilisateur s'interdit de :</p>
            <ul>
              <li>Copier, reproduire, modifier de quelque façon que ce soit, réaliser des interfaces, intégrer dans un autre produit, tout ou partie de l'Application, même pour corriger les erreurs qu'elle peut contenir, cette faculté étant exclusivement réservée à HAXO, à l'exception des copies autorisées par la loi ;</li>
              <li>Désassembler ou pratiquer quelque ingénierie inverse, essayer de découvrir les codes sources (strictement confidentiels) et/ou les fichiers de données propres à l'Application, y compris pour la rendre interopérable avec d'autres matériels ou logiciels que ceux mentionnés sur le descriptif de l'Application ;</li>
              <li>Distribuer, donner, vendre, sous-licencier, ou autrement transférer, même gratuitement, tout ou partie des droits conférés par ce contrat, par tout moyen, à qui que ce soit.</li>
            </ul>
          </section>

          <section>
            <h2>Garanties et responsabilités</h2>
            <p>Le Gestionnaire et l'Utilisateur doivent disposer des compétences, des matériels et des logiciels requis pour utiliser l'Application.</p>
            <p>HAXO s'efforce d'assurer le bon fonctionnement de l'Application dans la limite d'une obligation de moyens.</p>
            <p>L'Utilisateur est informé que l'Application peut être affectée par des interruptions, ralentissements, indisponibilités, opérations de maintenance, anomalies, contraintes de connectivité, ou plus généralement par tout aléa technique inhérent aux réseaux, terminaux mobiles, services tiers ou équipements déployés chez le Gestionnaire.</p>
            <p>HAXO ne garantit pas :</p>
            <ul>
              <li>Une disponibilité permanente et sans interruption de l'Application ;</li>
              <li>L'absence de bug ou d'anomalie ;</li>
              <li>La compatibilité avec tous les terminaux, systèmes, versions logicielles ou environnements réseaux ;</li>
              <li>L'ouverture effective de tout accès à chaque demande.</li>
            </ul>
          </section>

          <section>
            <h2>Durée et résiliation du droit d'utilisation de l'Application</h2>
            <p><strong>Durée :</strong> Les droits d'installation, d'accès et d'utilisation de l'Application sont concédés tant qu'un contrat entre le Gestionnaire et l'installateur des Produits HAXO pour l'utilisation des Produits HAXO est en vigueur.</p>
            <p><strong>Résiliation :</strong> Le Gestionnaire et, le cas échéant, HAXO dans les limites de ses droits et obligations, peuvent suspendre, restreindre ou supprimer l'accès d'un Utilisateur à l'Application en cas de perte de son habilitation, en cas de départ de l'Utilisateur ou de changement de fonctions, en cas de violation des présentes CGU, en cas d'usage frauduleux, abusif ou illicite ou en cas d'exigence de sécurité. La suspension ou la suppression du Compte n'ouvre droit à aucune indemnité, sauf faute démontrée d'HAXO.</p>
          </section>

          <section>
            <h2>Données personnelles</h2>
            <p>L'utilisation de l'Application implique des traitements de données personnelles concernant l'Utilisateur, notamment ses données d'identification, ses données de connexion, ses habilitations d'accès, ses actions dans l'Application et, le cas échéant, des données relatives à la vérification de sa position ou à l'utilisation d'un QR code.</p>
            <p>Le Gestionnaire intervient comme responsable de traitement de ces données, selon les moyens et finalités qu'il a définis. Les finalités, bases légales, destinataires, durées de conservation et modalités d'exercice des droits de l'Utilisateur sont disponibles auprès du Gestionnaire. L'Utilisateur peut exercer ses droits auprès du Gestionnaire, en sa qualité de responsable de traitement, ou auprès de toute autre entité désignée dans l'information qui lui est remise.</p>
            <p>Lorsqu'HAXO héberge l'Application ou opère techniquement certains traitements pour le compte de de l'installateur des Produits HAXO, lui-même opérant pour le compte du Gestionnaire, HAXO peut intervenir comme sous-traitant ultérieur, selon les modalités prévues dans le contrat entre HAXO et son distributeur autorisé qui lui-même a la responsabilité de fixer les modalités applicables l'installateur des Produits HAXO auquel il les a commercialisés.</p>
          </section>

          <section>
            <h2>Dispositions générales</h2>
            <p>Si l'une quelconque des clauses non-essentielles de ces CGU s'avérait nulle ou inapplicable en vertu d'une loi ou d'un règlement ou à la suite d'une décision exécutoire d'une juridiction ou d'une autorité compétente, les parties conviennent expressément que les Conditions Générales ne seront pas affectées par la nullité de la clause précitée.</p>
            <p>Le fait pour l'une des parties de ne pas exiger à un moment quelconque l'exécution stricte par l'autre partie d'une disposition ou condition quelconque des Conditions Générales ne sera pas réputé constituer une renonciation définitive à cette disposition ou condition. En conséquence, chacune des parties pourra à tout moment demander l'exécution stricte et intégrale par l'autre partie des dispositions et conditions de ces Conditions Générales.</p>
            <p>Les présentes CGU seront interprétées, exécutées et régies par le droit français (à l'exclusion de l'application des règles relatives aux conflits de lois et de juridictions).</p>
            <p>En cas de difficultés relatives à la validité, l'interprétation, l'exécution, l'inexécution, la résiliation des présentes CGU, celui-ci sera réglé entre le Gestionnaire et le l'installateur des Produits HAXO auprès duquel il a acquis les Produits HAXO, sauf intervention volontaire d'HAXO.</p>
          </section>
        </template>

        <template v-else>
          <section class="intro-section">
            <h1>Terms of Use — HAXO OPEN Mobile Application</h1>
            <p class="subtitle">End Users</p>
            <p class="version">Version dated April 13, 2026</p>

            <p>
              The HAXO OPEN application ("the Application") is made available to you under the contractual relationship established between, on one hand, the installer of HAXO Products, and on the other hand, your employer or any other entity on behalf of or for the benefit of which you use the HAXO OPEN application ("the Manager").
            </p>
            <p>
              You access the Application as a user authorized by the Manager, for its needs and under its responsibility, within the limits of the rights, authorizations and settings that have been assigned to you.
            </p>
            <p>
              These Terms of Use ("ToU") are intended to define the terms and conditions under which you are authorized to access and use the HAXO OPEN application. It is, however, recalled that the contractual relationship is established exclusively between the installer of HAXO Products and the Manager.
            </p>
          </section>

          <section>
            <h2>Definitions</h2>
            <p>Whenever used in these ToU, in the singular or plural, the following terms shall have the meanings set out below:</p>

            <dl class="definitions-list">
              <div>
                <dt>Application</dt>
                <dd>mobile application published by HAXO, called HAXO OPEN, accessible on a compatible smartphone, allowing the authorized User to use certain access control features linked to the Manager's equipment.</dd>
              </div>
              <div>
                <dt>Account</dt>
                <dd>the User's personal space, created by the Manager, enabling access to the Application using Credentials.</dd>
              </div>
              <div>
                <dt>General Terms and Conditions or ToU</dt>
                <dd>these terms and conditions applicable to the User, setting out the conditions for the User's use of the Application.</dd>
              </div>
              <div>
                <dt>Manager</dt>
                <dd>any legal or natural person who uses HAXO solutions for their own professional needs and who authorizes the User to access the Application, through the creation and management of authorizations and the configuration of access rights.</dd>
              </div>
              <div>
                <dt>HAXO</dt>
                <dd>a simplified joint-stock company (société par actions simplifiée), with its registered office at 368 Chemin du Chevalier, Quartier Bonneval, 83470 Saint Maximin la Sainte Baume, registered with the Draguignan Trade and Companies Register under number 934 957 820, represented by its President acting with the necessary authority.</dd>
              </div>
              <div>
                <dt>Credentials</dt>
                <dd>the User's email address and the password they choose or use to access the Application, as well as, where applicable, any other authentication method put in place.</dd>
              </div>
              <div>
                <dt>Reader</dt>
                <dd>any compatible hardware device enabling access control and, where applicable, the reading of a fixed or rotating QR code using the Application.</dd>
              </div>
              <div>
                <dt>HAXO Products</dt>
                <dd>the hardware, software, applications, interfaces and services designed, published, marketed or operated by HAXO, directly or indirectly.</dd>
              </div>
              <div>
                <dt>Rotating QR code</dt>
                <dd>a visual code with a limited validity period, periodically renewed, used for User authentication on a compatible Reader.</dd>
              </div>
              <div>
                <dt>User</dt>
                <dd>any natural person to whom the Manager has granted access rights to the Application for their authorized purposes.</dd>
              </div>
              <div>
                <dt>Device</dt>
                <dd>the User's mobile device (of which they are the sole owner or have control), equipped with a compatible Android or iOS operating system, on which the Application is installed and used.</dd>
              </div>
            </dl>
          </section>

          <section>
            <h2>Purpose</h2>
            <p>These ToU are intended to define the conditions for installing, accessing and using the Application by any User authorized by the Manager, who is a client of an installer of HAXO Products.</p>
            <p>The User uses the Application in the name, on behalf of or for the benefit of the Manager, and under the Manager's responsibility.</p>
            <p>HAXO reserves the right to update the Application and to amend these ToU at any time. In the event of a modification to the ToU or the Application, the User will be notified by any appropriate means within the Application or by any other appropriate means.</p>
          </section>

          <section>
            <h2>Application Description and Access</h2>
            <h3>Application Description</h3>
            <p>The Application enables natural persons authorized by the Manager, through a personal account, to access certain access control features associated with the equipment deployed by the Manager, in particular:</p>
            <ul>
              <li>Viewing the doors to which the User is authorized to access;</li>
              <li>Remotely opening certain doors, when this feature is enabled;</li>
              <li>Displaying the result of an opening request;</li>
              <li>Using a fixed or rotating QR code for authentication on a Reader;</li>
              <li>Managing display preferences, including doors saved as favorites.</li>
            </ul>
            <p>When enabled by the Manager, the remote opening feature allows the User to submit an opening request for certain doors for which they are authorized. An opening request does not guarantee that the door will actually open. Its processing depends in particular on the permissions assigned to the User, the operational status of the relevant equipment, the availability of networks and technical infrastructure, or the security or control rules configured by the Manager.</p>
            <p>Certain remote opening requests may be subject to verification of the User's geographic location, if this feature has been enabled by the Installer for the relevant Reader and if the User has granted location access to their Device.</p>
            <p>In that case, the Application may verify whether the Device is located within a defined perimeter around the relevant Reader, in order to allow or deny the opening request.</p>
            <p>The User is informed that:</p>
            <ul>
              <li>Location verification may be mandatory for certain accesses when the Installer has enabled it;</li>
              <li>The absence of geolocation authorization, the disabling of location services, insufficient GPS accuracy, network unavailability, or any positional inconsistency may prevent the remote opening;</li>
              <li>Geolocation does not entail continuous tracking of the User: it may be limited to the check performed at the time of an opening request, depending on the technical configuration on the User's Device.</li>
            </ul>
            <p>Depending on the settings chosen by the Manager, the Application may allow the User to display a rotating QR code used for authentication with a compatible Reader.</p>
            <p>When the QR code is rotating, its validity period is limited.</p>

            <h3>Application Access</h3>
            <p>The Application is reserved for Users who have been previously registered in the system and authorized by the Manager.</p>
            <p>From the application, the User creates their Account by entering their email address, password and authentication method. The User then receives an email with a link enabling them to activate their account. The link provided is valid for 24 hours.</p>
            <p>The Manager then invites the User by entering their email address from the HAXO KEY management interface.</p>
            <p>The User must have a compatible Device with the software versions, systems and settings required for the proper functioning of the Application, as well as Internet access.</p>
            <p>The User accesses the Application after downloading it to their Device from the download platform corresponding to the Device's operating system, and logs into their Account using their Credentials.</p>
            <p>The User acknowledges that Credentials are strictly personal and confidential and agrees not to share or disclose them to third parties. To prevent fraudulent use of Credentials, the User must choose a password that meets a high level of security according to current standards and recommendations. In the event of loss, theft, compromise or unauthorized use of the Device or Credentials, the User must notify the Manager without delay.</p>
            <p>Equipment costs, telecom subscriptions, network access fees and, more generally, all technical means required to use the Application remain the exclusive responsibility of the User or the Manager, depending on their internal organization.</p>
            <p>The Device must be equipped with a compatible version of the operating system, as indicated on the Application's download page. The Application may be updated. Where applicable, the technical prerequisites for installing or using an updated version of the Application will be specified on the corresponding page.</p>

            <h3>Acceptable Use of the Application</h3>
            <p>The User agrees to use the Application in accordance with its intended purpose, these ToU and any instructions provided, as well as to comply with the Manager's internal policies, security rules and guidelines.</p>
            <p>It is prohibited to attempt to circumvent authentication, authorization or security mechanisms, to use the Application to bypass the authorizations and access rules defined by the Manager, in particular by allowing third parties to enter through doors unlocked by the User, to reproduce, transmit, capture or share a QR code with a third party, or to disrupt the operation of the Application, networks, servers or associated equipment.</p>
          </section>

          <section>
            <h2>Intellectual Property Rights</h2>
            <p>HAXO holds all rights, titles or interests, or has the necessary authorizations, in respect of HAXO Products, the Application and, more generally, HAXO's distinctive signs.</p>
            <p>The User acknowledges that all intellectual and industrial property rights, copyrights, patents, trademarks, trade secrets, know-how, ideas, concepts and inventions, whether or not covered by an intellectual property title, relating to HAXO Products, distinctive signs and the Application, including but not limited to all modifications, translations, adaptations, improvements, corrections, updates or new versions, derivative works, compilations, and technical know-how, are and remain reserved to HAXO at all times.</p>
            <p>Subject to the existence and continued validity of a contract between the Manager and the installer of HAXO Products, the User is granted a personal, non-exclusive, non-transferable right, for the duration of the contract entered into by the Manager, to install and use the Application in executable form on their Device, strictly limited to the acts necessary for using the Application solely for the Manager's internal needs in connection with the use of HAXO Products, to the exclusion of any other purpose.</p>
            <p>Any use of the Application not authorized under these ToU is prohibited. In particular, but without limitation, the User agrees not to:</p>
            <ul>
              <li>Copy, reproduce, modify in any way, create interfaces, or incorporate into another product, all or part of the Application, even to correct errors it may contain, as this right is reserved exclusively to HAXO, except for copies authorized by law;</li>
              <li>Disassemble or reverse engineer, attempt to discover the source code (strictly confidential) and/or proprietary data files of the Application, including to make it interoperable with hardware or software other than those specified in the Application's description;</li>
              <li>Distribute, give, sell, sublicense or otherwise transfer, even free of charge, all or part of the rights granted under this contract, by any means, to anyone.</li>
            </ul>
          </section>

          <section>
            <h2>Warranties and Liability</h2>
            <p>The Manager and the User must have the skills, hardware and software required to use the Application.</p>
            <p>HAXO endeavors to ensure the proper functioning of the Application, subject to a best-efforts obligation.</p>
            <p>The User is informed that the Application may be affected by interruptions, slowdowns, unavailability, maintenance operations, anomalies, connectivity constraints, or more generally any technical hazard inherent to networks, mobile devices, third-party services or equipment deployed by the Manager.</p>
            <p>HAXO does not guarantee:</p>
            <ul>
              <li>Permanent, uninterrupted availability of the Application;</li>
              <li>The absence of bugs or anomalies;</li>
              <li>Compatibility with all devices, systems, software versions or network environments;</li>
              <li>The effective opening of any access upon each request.</li>
            </ul>
          </section>

          <section>
            <h2>Term and Termination of the Right to Use the Application</h2>
            <p><strong>Term:</strong> The rights to install, access and use the Application are granted for as long as a contract between the Manager and the installer of HAXO Products for the use of HAXO Products is in force.</p>
            <p><strong>Termination:</strong> The Manager and, where applicable, HAXO within the limits of its rights and obligations, may suspend, restrict or delete a User's access to the Application in the event of loss of authorization, departure of the User or change of duties, breach of these ToU, fraudulent, abusive or unlawful use, or for security requirements. Suspension or deletion of the Account does not give rise to any compensation, unless HAXO is proven to be at fault.</p>
          </section>

          <section>
            <h2>Personal Data</h2>
            <p>Use of the Application involves the processing of personal data relating to the User, including identification data, connection data, access authorizations, actions within the Application and, where applicable, data relating to the verification of the User's location or use of a QR code.</p>
            <p>The Manager acts as data controller for such data, in accordance with the purposes and means it has defined. The purposes, legal bases, recipients, retention periods and means of exercising the User's rights are available from the Manager. The User may exercise their rights with the Manager, in its capacity as data controller, or with any other entity designated in the information provided to them.</p>
            <p>Where HAXO hosts the Application or technically operates certain processing operations on behalf of the installer of HAXO Products, who itself operates on behalf of the Manager, HAXO may act as a sub-processor, in accordance with the terms set out in the contract between HAXO and its authorized distributor, who is itself responsible for setting the applicable terms with the installer of HAXO Products to whom they have marketed such services.</p>
          </section>

          <section>
            <h2>General Provisions</h2>
            <p>If any non-essential clause of these ToU is found to be null and void or unenforceable under a law or regulation or following an enforceable decision by a competent court or authority, the Parties expressly agree that the General Terms and Conditions shall not be affected by the nullity of said clause.</p>
            <p>The failure of either Party to require strict performance by the other Party of any provision or condition of the General Terms and Conditions at any time shall not be deemed to constitute a definitive waiver of that provision or condition. Accordingly, each Party may at any time demand strict and full compliance by the other Party with the provisions and conditions of these General Terms and Conditions.</p>
            <p>These ToU shall be interpreted, performed and governed by French law (to the exclusion of the application of conflict of law rules).</p>
            <p>In the event of difficulties relating to the validity, interpretation, performance, non-performance or termination of these ToU, any dispute shall be settled between the Manager and the installer of HAXO Products from whom they acquired the HAXO Products, unless HAXO voluntarily intervenes.</p>
          </section>
        </template>

      </article>
    </main>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
const { locale } = useI18n();
</script>

<style scoped>
.cgu-page {
  --gradient: linear-gradient(135deg, #35009f, #506cf0);
  --color-text: #1a1a1a;
  --color-subtext: #6b6b6b;
  --color-bg: #ffffff;
  --color-border: #e0e0e0;
  --color-primary: #506cf0;
  --color-card: #ffffff;
  background-color: var(--color-bg);
  color: var(--color-text);
  font-family: 'Inter', Arial, sans-serif;
  line-height: 1.7;
  min-height: 100vh;
}

.top-banner {
  background: linear-gradient(0deg, #6c2ecf 0%, #281ca6 40%, #22096f 70%);
  color: white;
  padding: 1rem;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.banner-content {
  align-items: center;
  display: flex;
  justify-content: center;
}

.logo {
  height: 56px;
  max-width: 180px;
  object-fit: contain;
}

.container {
  margin: 0 auto;
  max-width: 960px;
  padding: 40px 20px;
}

.cgu-card {
  background: var(--color-card);
  border: 1px solid var(--color-border);
  border-radius: 18px;
  box-shadow: 0 12px 30px rgba(34, 9, 111, 0.08);
  padding: 40px;
}

.intro-section {
  border-bottom: 1px solid var(--color-border);
  margin-bottom: 36px;
  padding-bottom: 24px;
}

h1 {
  color: var(--color-primary);
  font-size: 30px;
  font-weight: 700;
  line-height: 1.25;
  margin: 0 0 12px;
  text-transform: uppercase;
}

.subtitle,
.version {
  color: var(--color-subtext);
  font-weight: 700;
  letter-spacing: 0.03em;
  margin: 4px 0;
  text-transform: uppercase;
}

.version {
  margin-bottom: 28px;
}

h2 {
  border-bottom: 2px solid var(--color-border);
  color: var(--color-primary);
  font-size: 22px;
  margin: 48px 0 18px;
  padding-bottom: 8px;
  text-transform: uppercase;
}

h3 {
  color: #281ca6;
  font-size: 18px;
  margin: 30px 0 12px;
}

p {
  margin: 14px 0;
  text-align: justify;
}

ul {
  margin: 12px 0 20px 22px;
  padding: 0;
}

li {
  margin: 8px 0;
  padding-left: 4px;
}

.definitions-list {
  margin: 18px 0 0;
}

.definitions-list div {
  border-bottom: 1px solid var(--color-border);
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(160px, 240px) 1fr;
  padding: 14px 0;
}

.definitions-list div:last-child {
  border-bottom: 0;
}

dt {
  color: #281ca6;
  font-weight: 700;
}

dd {
  margin: 0;
  text-align: justify;
}

strong {
  color: #281ca6;
}

@media (max-width: 700px) {
  .container {
    padding: 24px 14px;
  }

  .cgu-card {
    border-radius: 14px;
    padding: 24px 18px;
  }

  h1 {
    font-size: 24px;
  }

  h2 {
    font-size: 20px;
    margin-top: 38px;
  }

  .definitions-list div {
    gap: 4px;
    grid-template-columns: 1fr;
  }
}
</style>
